/**
 * Sales. The one flow where invalidation and optimism have to be exactly right, because a
 * salesperson is standing at a counter with a customer waiting.
 */

import { api } from '../api.js';
import { receiptTag, saleListTag, saleTag, stockTag, unitTag } from '../tags.js';
import type {
  CreateSaleRequest,
  Paged,
  Receipt,
  ReversalRequestRow,
  Sale,
  SaleId,
  SalesSummary,
  SaleStatus,
  SaleSummary,
  ShopId,
  UserId,
  VariantId,
} from '../../types/api.js';

export type SalesSummaryParams = {
  readonly shopId?: ShopId | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
};

export type SaleListParams = {
  readonly shopId: ShopId;
  /** `today` and `mine` are the two the POS uses; the dashboard passes a filter hash. */
  readonly scope: string;
  readonly userId?: UserId | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly status?: SaleStatus | undefined;
  readonly search?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly sort?: string | undefined;
  /** One customer's purchases, for the Customers page. */
  readonly customerId?: string | undefined;
};

export type CreateSaleArgs = {
  readonly body: CreateSaleRequest;
  /**
   * Generated before the user taps Complete and reused on every retry, so a resubmission after a
   * dropped connection returns the original sale rather than ringing it up twice.
   */
  readonly idempotencyKey: string;
};

export const salesApi = api.injectEndpoints({
  endpoints: (build) => ({
    createSale: build.mutation<Sale, CreateSaleArgs>({
      query: ({ body, idempotencyKey }) => ({
        url: '/sales',
        method: 'POST',
        body,
        idempotencyKey,
      }),

      // Exactly what changed, and nothing else. Not `Variant` — the variant row did not change,
      // its stock did. Not `SaleList: LIST` — a till has no business refreshing a report nobody
      // is looking at. A misc. line has no variant id at all — nothing here to invalidate for it,
      // since it moved no stock in the first place.
      invalidatesTags: (_result, _error, { body }) => [
        ...body.lines
          .map((line) => line.variantId)
          .filter((variantId): variantId is VariantId => variantId !== null)
          .map((variantId) => stockTag(body.shopId, variantId)),
        ...body.lines
          .filter((line) => line.variantId !== null && line.inventoryUnitId !== null)
          .map((line) => unitTag(body.shopId, line.variantId!)),
        saleListTag(body.shopId, 'today'),
        saleListTag(body.shopId, 'mine'),
      ],

      /**
       * The sale appears in today's list the moment it is submitted, and disappears again if the
       * server rejects it. A spinner with a customer waiting is the thing this avoids.
       */
      async onQueryStarted({ body, idempotencyKey }, { dispatch, queryFulfilled }) {
        const optimistic: SaleSummary = {
          id: idempotencyKey as SaleId,
          receiptNo: '…',
          soldAt: body.soldAt,
          shopId: body.shopId,
          shopName: '',
          soldByUserId: '' as UserId,
          soldByName: '',
          lineCount: body.lines.length,
          totalKes: body.lines.reduce((total, line) => total + line.unitPriceKes * line.quantity, 0),
          priceType: body.priceType,
          status: 'completed',
          customerName: body.customerName,
          hasOpenReversalRequest: false,
        };

        // Inlined rather than extracted: `dispatch` here carries the store's own type, and a
        // helper would have to widen it to `unknown` and cast its way back.
        const patches = (['today', 'mine'] as const).map((scope) =>
          dispatch(
            salesApi.util.updateQueryData('listSales', { shopId: body.shopId, scope }, (draft) => {
              draft.items.unshift(optimistic);
              draft.total += 1;
            }),
          ),
        );

        try {
          const { data } = await queryFulfilled;

          // Swap the placeholder for the real row, so the receipt number stops being an ellipsis
          // without waiting for the invalidation round trip.
          for (const scope of ['today', 'mine'] as const) {
            dispatch(
              salesApi.util.updateQueryData('listSales', { shopId: body.shopId, scope }, (draft) => {
                const index = draft.items.findIndex((item) => item.id === optimistic.id);
                if (index >= 0) draft.items[index] = data;
              }),
            );
          }
        } catch {
          // Rolled back visibly: the row vanishes and the sale screen shows why. The cart is
          // restored by the feature, which still holds it — see `sale-checkout`.
          for (const patch of patches) patch.undo();
        }
      },
    }),

    listSales: build.query<Paged<SaleSummary>, SaleListParams>({
      query: ({ scope: _scope, ...params }) => ({
        url: '/sales',
        params: { page: 1, pageSize: 25, sort: 'soldAt:desc', ...params },
      }),
      providesTags: (result, _error, { shopId, scope }) => [
        saleListTag(shopId, scope),
        ...(result?.items ?? []).map((sale) => saleTag(sale.id)),
      ],
    }),

    getSale: build.query<Sale, SaleId>({
      query: (id) => `/sales/${id}`,
      providesTags: (_result, _error, id) => [saleTag(id)],
    }),

    requestReversal: build.mutation<Sale, { saleId: SaleId; shopId: ShopId; reason: string }>({
      query: ({ saleId, reason }) => ({
        url: `/sales/${saleId}/reversal-request`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { saleId, shopId }) => [
        saleTag(saleId),
        saleListTag(shopId, 'today'),
        saleListTag(shopId, 'mine'),
        { type: 'ReversalRequestList', id: 'LIST' },
      ],
    }),

    /** Sales waiting for a manager's decision, oldest first. Scoped to the manager's shop by the server. */
    listReversalRequests: build.query<ReversalRequestRow[], void>({
      query: () => '/sales/reversal-requests',
      providesTags: [{ type: 'ReversalRequestList', id: 'LIST' }],
    }),

    /**
     * Approve puts the goods back on the shelf and marks the sale reversed; refuse needs a note,
     * which the salesperson sees. Either way the sale, every sale list, and the stock change.
     */
    decideReversal: build.mutation<void, { saleId: SaleId; approve: boolean; note?: string | undefined }>({
      query: ({ saleId, approve, note }) => ({
        url: `/sales/${saleId}/reversal-approval`,
        method: 'POST',
        body: { approve, note: note ?? null },
      }),
      invalidatesTags: (_result, _error, { saleId }) => [
        saleTag(saleId),
        receiptTag(saleId),
        { type: 'ReversalRequestList', id: 'LIST' },
        'SaleList',
        'Stock',
        'Unit',
      ],
    }),

    /**
     * An exact aggregate for a window — count, revenue, average ticket. Never derive this from
     * `listSales`'s paginated `items`, which silently undercounts past one page.
     */
    getSalesSummary: build.query<SalesSummary, SalesSummaryParams>({
      query: (params) => ({ url: '/sales/summary', params }),
    }),

    getReceipt: build.query<Receipt, SaleId>({
      query: (id) => `/sales/${id}/receipt`,
      providesTags: (_result, _error, id) => [receiptTag(id)],
    }),

    /**
     * A reprint is a fraud-adjacent event, so it is recorded. Invalidates the receipt only — the
     * sale itself did not change, and refetching it would be a lie about what happened.
     */
    logReceiptPrint: build.mutation<void, { saleId: SaleId; isReprint: boolean }>({
      query: ({ saleId, isReprint }) => ({
        url: `/sales/${saleId}/receipt-print`,
        method: 'POST',
        body: { isReprint },
      }),
      invalidatesTags: (_result, _error, { saleId }) => [receiptTag(saleId)],
    }),
  }),
});

export const {
  useCreateSaleMutation,
  useListSalesQuery,
  useGetSaleQuery,
  useGetSalesSummaryQuery,
  useRequestReversalMutation,
  useListReversalRequestsQuery,
  useDecideReversalMutation,
  useGetReceiptQuery,
  useLogReceiptPrintMutation,
} = salesApi;
